
'use client';

import {
  BsFacebook,
  BsTwitter,
  BsLinkedin,
} from 'react-icons/bs';
import { FaLink } from 'react-icons/fa';
import { Image } from '@nextui-org/react';
import { useCallback } from 'react';

import { useClientTranslation } from '@/hook/translate';
import { generateShareLink } from '@/utils';
import { SharePlatform } from '@/types';
import { MindfulAudioImg } from '@/config/img/mindfulAudio';
import AudioBar from './AudioBar';
import { mindful } from '@/types/audioResData';
import useAudio from '@/hook/useAudio';
import MdPlayer from './MdPlayer';
import MdContent from './MdContent';
import Link from 'next/link';


interface IProps {
  title: string;
  author: string;
  subTitle: string;
  update: string;
  score: string;
  rate: string;
  isFull: boolean;
  youtubeId?: string;
  about: mindful.TAbout;
  chapters: mindful.TChapter[];
  audioPath: string;
}

export default function MdAudioContent(props: IProps) {
  const { t } = useClientTranslation();
  const audioURL = props.audioPath || '';
  const { about } = props;
  const {
    play,
    pause,
    duration,
    audioStatus,
    currentTime,
    changeCurrentTime,
  } = useAudio(audioURL);

  const handleShareCLick = useCallback((platform: SharePlatform) => {
    const url = window.location.href;
    const shareLink = generateShareLink(platform, {
      url,
      title: props.title,
    });
    window.open(shareLink || '');

  }, [props.title]);
  return (
    <div>
      <div className="card bg-[#E7FBB0] p-10 rounded-[50px] text-center  mt-[50px]">
        <h2 className=' text-[52px] leading-[71px] font-black'>{props.title} {props.isFull ? t('fullAudio', 'mindfulAudio') : t('summaryAudio', 'mindfulAudio')}</h2>
        <p className='text-28-43 pt-3'>{props.author}</p>
        <p className='text-[#191300] text-20-27 pt-3'>{props.subTitle}</p>
        <div className='flex items-center justify-center py-5' key={'star'}>
          <div className='star-wrap flex justify-center'>
            <Image src={MindfulAudioImg.Star} alt='stars' width={22} height={22} />
          </div>
          <span className='text-[#191300] text-18-27 pl-[5px]'>{props.score}</span>
          <span className='text-[rgba(0,0,0,0.5)] text-xs pl-[10px]'>
            <span className='pr-1'>{props.rate}</span>
            <span className='pr-1'>{t('rating', 'mindfulAudio')}</span>
            <span className='pr-1'>({t('readerRefer', 'mindfulAudio')})</span>
          </span>
        </div>
        <div className='share-container flex items-center justify-center pt-3 pb-36' key={'share'}>
          <BsTwitter className='w-7 h-7 text-[#98a377] mr-[10px] cursor-pointer' onClick={() => handleShareCLick(SharePlatform.Twitter)} />
          <BsFacebook className='w-7 h-7 text-[#98a377] mr-[10px] cursor-pointer' onClick={() => handleShareCLick(SharePlatform.Facebook)} />
          <BsLinkedin className='w-7 h-7 text-[#98a377] mr-[10px] cursor-pointer' onClick={() => handleShareCLick(SharePlatform.Linkedin)} />
          <FaLink className='w-7 h-7 text-[#98a377] mr-[10px] cursor-pointer' onClick={() => handleShareCLick(SharePlatform.Link)} />
        </div>
        {/* <AudioBar url={ props.url } /> */}
        {
          props.youtubeId ?
            <MdPlayer videoId={props.youtubeId} />
            :
            <AudioBar 
            play={play} 
            pause={pause} 
            duration={duration} 
            audioStatus={audioStatus} 
            currentTime={currentTime} 
            changeCurrentTime={changeCurrentTime} 
            url={audioURL} 
            />
        }
        <div className='text-left '>
          <Link href={`https://www.youtube.com/watch?v=${props?.youtubeId}`} target='_blank'>
            <span className='text-12-17 text-[rgba(0,0,0,0.5)]'>{props.youtubeId ? t('sourceYou', 'mindfulAudio') : ''}</span>
          </Link>
        </div>

      </div>
      <div className='bg-[rgba(255,255,255,0.08)] rounded-[40px] pt-[60px]'>
        <h2 className=' text-40-55 font-extrabold'>
          {t('information', 'mindfulAudio')}
        </h2>
        <div className="about flex items-center pt-[15px]">
          <Image width={180} height={270} src={about.url} radius='none' className='rounded-r-xl' alt='cover' />
          <div className="info text-20-27 ml-[50px]">
            <p>
              <span className='font-semibold'>{t('author', 'mindfulAudio')}:</span>
              <span> {about.author}</span>
            </p>
            <p className='pt-3'>
              <span className='font-semibold'>{t('narrator', 'mindfulAudio')}:</span>
              <span> {about.narrator}</span>
            </p>
            <p  className='pt-3'>
              <span className='font-semibold'>{t('format', 'mindfulAudio')}:</span>
              <span> {about.format}</span>
            </p>
            <p  className='pt-3'>
              <span className='font-semibold'>{t('ibsn', 'mindfulAudio')}:</span>
              <span> {about.ISBN || '-'}</span>
            </p>
            <p  className='pt-3'>
              <span className='font-semibold'>{t('language', 'mindfulAudio')}:</span>
              <span> {about.language}</span>
            </p>
            <p  className='pt-3'>
              <span className='font-semibold'>{t('publishDate', 'mindfulAudio')}:</span>
              <span> {about.publishDate}</span>
            </p>
            <p  className='pt-3'>
              <span className='font-semibold'>{t('audioLength', 'mindfulAudio')}:</span>
              <span> {about.times}</span>
              <span>{ t('minute', 'mindfulAudio') }</span>
            </p>
          </div>
        </div>
      </div>
      <MdContent 
        chapters={ props.chapters }
        showTitle={ !!props.youtubeId } 
        title={ props.title }  
        play={play} 
        pause={ pause } 
        audioStatus={ audioStatus }
      />
    </div>
  );
}
